import { Component, OnChanges, Input, SimpleChanges, Injector, OnInit } from '@angular/core';
import { FormControl, Validators, FormGroup, ValidationErrors } from '@angular/forms';
import { ApAlertService } from 'webcommon/core-ui';
import { FcListValidationService } from '../../generic/fc-list-validation/fc-list-validation.service';
import { FcItemModalContext, FcItemModalMode } from '../../../models';
import {
    FcAccountTypeDataDto,
    FcAutoWriteoffAdjTypeDataDTO,
    FcClaimDefaultOptionsDataDto,
    FcClaimOptionsDataDto,
    FcFeeScheduleDataDto,
    FcInsurancePlanListItemDto,
    FcReimbursementRuleDataDto,
    FindControlAdditionalSearchParametersType,
    FindControlRequiredSearchParametersDto,
    FormGroupValidationRules,
    SystemGuid
} from 'webcommon/shared';
import { FormControlExtended } from '../../generic';
import { FcItemListBaseComponent } from '../../generic';
import { MapInsuranceNetworkService } from '../map-insurance-network/map-insurance-network.service';
import { MatRadioChange } from '@angular/material/radio';

@Component({
    providers: [ApAlertService, FcListValidationService],
    selector: 'fc-insurance-plan-settings-tab',
    styleUrls: ['./fc-insurance-plan-settings-tab.component.less'],
    templateUrl: './fc-insurance-plan-settings-tab.component.html',
})
export class FcInsurancePlanSettingTabComponent extends FcItemListBaseComponent implements OnInit, OnChanges {
    private static readonly FeeScheduleName = 'FeeSchedule';
    @Input() baseId: string;
    @Input() form: FormGroup;
    @Input() listItem: FcInsurancePlanListItemDto;
    @Input() isSubmitted = false;
    @Input() itemContext: FcItemModalContext;
    @Input() isDataLoaded: boolean;

    public isNewMode: boolean;
    public selectedAccountTypeControlPks: string[] = [];
    public selectedFeeScheduleControlPks: string[] = [];
    public selectedAmountAllowedListControlPks: string[] = [];
    public selectedPaperClaimOptionsControlPks: string[] = [];
    public selectedElectronicClaimOptionsControlPks: string[] = [];
    public selectedClaimDefaultOptionsControlPks: string[] = [];
    public selectedReimbursementRuleControlPks: string[] = [];
    public selectedAutoWriteoffAdjTypeControlPks: string[] = [];

    public checkPayersFee = false;
    public checkProvidersFee = false;

    // Form Controls
    public accountTypeUidControl: FormControl = new FormControl<string>('');
    public feeScheduleUidControl: FormControl = new FormControlExtended<string>('');
    public amountAllowedFeeScheduleUidControl: FormControl = new FormControlExtended<string>('');
    public paperClaimFormatUidControl: FormControl = new FormControlExtended<string>('');
    public electronicClaimFormatUidControl: FormControl = new FormControlExtended<string>('');
    public paperClaimDefaultOptionUidControl: FormControl = new FormControlExtended<string>('');
    public memberIdFormatControl: FormControl = new FormControl<string>('');
    public groupIdFormatControl: FormControl = new FormControl<string>('');
    public reimbursementRuleUidControl: FormControl = new FormControlExtended<string>('');
    public autoWriteoffCreditTypeUidControl = new FormControlExtended<string>('');
    public referralRequiredControl: FormControl = new FormControl<boolean>(false);
    public noNetworkControl: FormControl = new FormControl<boolean>(false);

    public useInsuranceCarrierAccountTypeControl: FormControl = new FormControl<boolean>(false);
    public useProviderFeeScheduleControl: FormControl = new FormControl<boolean>(false);
    public useCarrierFeeScheduleControl: FormControl = new FormControl<boolean>(false);
    public useCarrierAmountAllowedFeeScheduleControl: FormControl = new FormControl<boolean>(false);
    public useCarrierPaperClaimFormatControl: FormControl = new FormControl<boolean>(false);
    public useCarrierElectronicClaimFormatControl: FormControl = new FormControl<boolean>(false);
    public useCarrierPaperClaimDefaultOptionControl: FormControl = new FormControl<boolean>(false);
    public useCarrierMemberIDFormatControl: FormControl = new FormControl<boolean>(false);
    public useCarrierGroupIDFormatControl: FormControl = new FormControl<boolean>(false);
    public useCarrierAutoWriteoffCreditTypeControl: FormControl = new FormControl<boolean>(false);
    public userCarrierReferralRequiredControl: FormControl = new FormControl<boolean>(false);
    public useCarrierNoNetworkControl: FormControl = new FormControl<boolean>(false);

    public claimDefaultOptionsRequiredSearchParameters = {
        Type: FindControlRequiredSearchParametersDto,
        RequiredSearchParameters: [
            {
                ColumnName: 'ListType',
                Negate: false,
                Value: 'PaperClaimOption',
            },
        ],
    };

    public paperClaimAdditonalSearchParameters = {
        Type: FindControlAdditionalSearchParametersType.Standard,
        AdditionalSearchParameters: [
            {
                ColumnName: 'IsPaper',
                Value: '1',
            },
        ],
    };

    public electronicClaimAdditonalSearchParameters = {
        Type: FindControlAdditionalSearchParametersType.Standard,
        AdditionalSearchParameters: [
            {
                ColumnName: 'IsPaper',
                Value: '0',
            },
        ],
    };

    public listItemDefaultValidation: FormGroupValidationRules = {
        AccountTypeUid: {
            IsRequired: false,
        },
        FeeScheduleUid: {
            IsRequired: false,
        },
        AmountAllowedFeeScheduleUid: {
            IsRequired: false,
        },
        PaperClaimFormatUid: {
            IsRequired: false
        },
        ElectronicClaimFormatUid: {
            IsRequired: false
        },
        PaperClaimDefaultOptionUid: {
            IsRequired: false
        },
        MemberIDFormat: {
            IsRequired: false,
            MaxLength: 50,
        },
        GroupIDFormat: {
            IsRequired: false,
            MaxLength: 50,
        },
        ReimbursementRuleUid: {
            IsRequired: false,
        },
        AutoWriteoffCreditTypeUid: {
            IsRequired: true,
        },
    };

    constructor(
        injector: Injector,
        public alertService: ApAlertService,
        private mapInsuranceNetworkService: MapInsuranceNetworkService,
    ) {
        super(injector);
    }

    public ngOnInit() {
        this.setDefaultValuesIntoForm();
    }

    public ngOnChanges(changes: SimpleChanges): void {
        if (changes.itemContext != null) {
            this.isNewMode = this.itemContext.mode === FcItemModalMode.New;
            this.fcListValidation.initValidation(this.itemContext, this.listItemDefaultValidation);
            this.fcListValidation.initValidation(this.itemContext, {
                [`MemberIDFormat`]: {
                    RegExp: '([Aa9 ?\-]*)',
                    MaxLength: 50,
                },
                [`GroupIDFormat`]: {
                    RegExp: '([Aa9 ?\-]*)',
                    MaxLength: 50,
                },
            });
        }

        if (changes.form != null) {
            this.addControlsToForm();
        }
        if ((changes.itemContext != null || changes.form != null || changes.listItem != null) && this.isDataLoaded) {
            this.setForm();
        }
    }

    public onRadioChange(
        event: MatRadioChange,
        controlName: FormControl,
        controlNameToSet: FormControl | null,
        propertyName?: string): void {
        const selectedValue = event.value;
        this.form.markAsDirty();
        switch (selectedValue) {
            case 'Other':
                controlName.enable();
                if (propertyName) {
                    this.changeValidators(controlName, propertyName, true);
                }
                if (event.source.name === FcInsurancePlanSettingTabComponent.FeeScheduleName) {
                    this.useProviderFeeScheduleControl.setValue(false);
                    this.useCarrierFeeScheduleControl.setValue(false);
                }
                break;
            case 'Payers':
                controlName.disable();
                if (propertyName) {
                    this.changeValidators(controlName, propertyName, false);
                }
                if (event.source.name === FcInsurancePlanSettingTabComponent.FeeScheduleName) {
                    this.useProviderFeeScheduleControl.setValue(false);
                    this.useCarrierFeeScheduleControl.setValue(true);
                }
                break;
            case 'Providers':
                controlName.disable();
                if (propertyName) {
                    this.changeValidators(controlName, propertyName, false);
                }
                this.useProviderFeeScheduleControl.setValue(true);
                this.useCarrierFeeScheduleControl.setValue(false);
                break;
            default:
                break;
        }
        if (controlNameToSet) {
            const valueToSet = !!controlName.disabled;
            controlNameToSet.setValue(valueToSet);
        }
    }

    public clearPaperClaimFields(): void {
        this.selectedPaperClaimOptionsControlPks = [];
        this.paperClaimFormatUidControl.setValue('');
    }

    public clearElectronicClaimFields(): void {
        this.selectedElectronicClaimOptionsControlPks = [];
        this.electronicClaimFormatUidControl.setValue('');
    }

    public clearDefaultClaimFields(): void {
        this.selectedClaimDefaultOptionsControlPks = [];
        this.paperClaimDefaultOptionUidControl.setValue('');
    }

    public clearFormControl(controlName: FormControl): void {
        controlName.setValue('');
    }

    private changeValidators(controlName: FormControl | FormControlExtended<string>, propertyName: string, required: boolean): void {
        const validators = this.fcListValidation.getValidators(propertyName);
        this.listItemDefaultValidation[propertyName].IsRequired = required;
        if (required) {
            validators.push(Validators.required);
            if (controlName instanceof FormControlExtended) {
                controlName.showErrorFlag = true;
            }
        }
        controlName.setValidators(validators);
        controlName.updateValueAndValidity();
    }

    public onMapNetworkClick(): void {
        const result = this.mapInsuranceNetworkService.openModal(this.baseId, false, this.listItem.Id);
        result.then((response: boolean) => {
            if (response) {
                this.form.markAsDirty();
            }
        });
    }

    public getFindControlValidation(controlName: string): boolean {
        if (this.fcListValidation.validations[controlName]) {
            return !!this.fcListValidation.validations[controlName].IsRequired;
        } else {
            return !!this.listItemDefaultValidation[controlName].IsRequired;
        }
    }

    public validateCreditType(): void {
        const validators = this.fcListValidation.getValidators('AutoWriteoffCreditTypeUid');
        validators.push((control: FormControlExtended<string>): ValidationErrors | null => {
        if (control.additionalData?.Name && !(control.additionalData?.TransactionTypeUid === SystemGuid.TransactionType_Adjustment)) {
            return {
                autoWriteoffAdjType: {
                name: control.additionalData.Name,
                }
            };
        }

        return null;
        });
        this.autoWriteoffCreditTypeUidControl.setValidators(validators);
    }

    public onAccountTypeDataPopulate(accountTypeArray: FcAccountTypeDataDto[]): void {
        const accountTypeData: FcAccountTypeDataDto = { ...accountTypeArray[0] };
        if (accountTypeData.Name) {
            this.accountTypeUidControl.setValue(accountTypeData.AccountTypeUid);
        } else {
            this.accountTypeUidControl.setValue(this.listItem.AccountTypeUid);
        }
    }

    public onFeeScheduleDataPopulate(feeScheduleArray: FcFeeScheduleDataDto[]): void {
        const feeScheduleData: FcFeeScheduleDataDto = { ...feeScheduleArray[0] };
        if (feeScheduleData.Name) {
            this.feeScheduleUidControl.setValue(feeScheduleData.FeeScheduleUid);
        } else {
            this.feeScheduleUidControl.setValue(this.listItem.FeeScheduleUid);
        }
    }

    public onAmountAllowedListDataPopulate(amountAllowedListArray: FcFeeScheduleDataDto[]): void {
        const amountAllowedListData: FcFeeScheduleDataDto = { ...amountAllowedListArray[0] };
        if (amountAllowedListData.Name) {
            this.amountAllowedFeeScheduleUidControl.setValue(amountAllowedListData.FeeScheduleUid);
        } else {
            this.amountAllowedFeeScheduleUidControl.setValue(this.listItem.AmountAllowedFeeScheduleUid);
        }
    }

    public onPaperClaimOptionsControlDataPopulate(paperClaimOptions: FcClaimOptionsDataDto[]): void {
        const paperClaimOptionsData: FcClaimOptionsDataDto = { ...paperClaimOptions[0] };
        if (paperClaimOptionsData.Name) {
            this.paperClaimFormatUidControl.setValue(paperClaimOptionsData.ClaimFormatUid);
        } else {
            this.paperClaimFormatUidControl.setValue(this.listItem.PaperClaimFormatUid);
        }
    }

    public onElectronicClaimOptionsControlDataPopulate(electroniClaimOptions: FcClaimOptionsDataDto[]): void {
        const electronicClaimOptionsData: FcClaimOptionsDataDto = { ...electroniClaimOptions[0] };
        if (electronicClaimOptionsData.Name) {
            this.electronicClaimFormatUidControl.setValue(electronicClaimOptionsData.ClaimFormatUid);
        } else {
            this.electronicClaimFormatUidControl.setValue(this.listItem.ElectronicClaimFormatUid);
        }
    }

    public onClaimDefaultOptionsControlDataPopulate(claimDefaultOptions: FcClaimDefaultOptionsDataDto[]): void {
        const claimDefaultOptionsData: FcClaimDefaultOptionsDataDto = { ...claimDefaultOptions[0] };
        if (claimDefaultOptionsData.Name) {
            this.paperClaimDefaultOptionUidControl.setValue(claimDefaultOptionsData.MiscUid);
        } else {
            this.paperClaimDefaultOptionUidControl.setValue(this.listItem.PaperClaimDefaultOptionUid);
        }
    }

    public onReimbursementRuleDataPopulate(reimbursementRuleArray: FcReimbursementRuleDataDto[]): void {
        const reimbursementRuleData: FcReimbursementRuleDataDto = { ...reimbursementRuleArray[0] };
        if (reimbursementRuleData.Name) {
            this.reimbursementRuleUidControl.setValue(reimbursementRuleData.ReimbursementRuleUid);
        } else {
            this.reimbursementRuleUidControl.setValue(this.listItem.ReimbursementRuleUid);
        }
    }

    public onAutoWriteoffAdjTypeDataPopulate(autoWriteoffAdjTypeArray: FcAutoWriteoffAdjTypeDataDTO[]): void {
        const autoWriteoffAdjTypeData: FcAutoWriteoffAdjTypeDataDTO = { ...autoWriteoffAdjTypeArray[0] };
        this.autoWriteoffCreditTypeUidControl.additionalData = autoWriteoffAdjTypeData as unknown as Record<string, string>;
        if (autoWriteoffAdjTypeData.Name) {
            this.validateCreditType();
            this.autoWriteoffCreditTypeUidControl.setValue(autoWriteoffAdjTypeData.CreditTypeUid);
        } else {
            this.autoWriteoffCreditTypeUidControl.setValue('');
            this.changeValidators(this.autoWriteoffCreditTypeUidControl, 'AutoWriteoffCreditTypeUid', true);
        }
    }

    private addControlsToForm(): void {
        this.form.addControl('accountTypeUid', this.accountTypeUidControl);
        this.form.addControl('feeScheduleUid', this.feeScheduleUidControl);
        this.form.addControl('amountAllowedFeeScheduleUid', this.amountAllowedFeeScheduleUidControl);
        this.form.addControl('paperClaimFormatUid', this.paperClaimFormatUidControl);
        this.form.addControl('electronicClaimFormatUid', this.electronicClaimFormatUidControl);
        this.form.addControl('paperClaimDefaultOptionUid', this.paperClaimDefaultOptionUidControl);
        this.form.addControl('memberIdFormat', this.memberIdFormatControl);
        this.form.addControl('groupIdFormat', this.groupIdFormatControl);
        this.form.addControl('reimbursementRuleUid', this.reimbursementRuleUidControl);
        this.form.addControl('autoWriteoffCreditTypeUid', this.autoWriteoffCreditTypeUidControl);
        this.form.addControl('referralRequired', this.referralRequiredControl);
        this.form.addControl('noNetwork', this.noNetworkControl);

        this.form.addControl('useInsuranceCarrierAccountType', this.useInsuranceCarrierAccountTypeControl);
        this.form.addControl('useProviderFeeSchedule', this.useProviderFeeScheduleControl);
        this.form.addControl('useCarrierFeeSchedule', this.useCarrierFeeScheduleControl);
        this.form.addControl('useCarrierAmountAllowedFeeSchedule', this.useCarrierAmountAllowedFeeScheduleControl);
        this.form.addControl('useCarrierPaperClaimFormat', this.useCarrierPaperClaimFormatControl);
        this.form.addControl('useCarrierElectronicClaimFormat', this.useCarrierElectronicClaimFormatControl);
        this.form.addControl('useCarrierPaperClaimDefaultOption', this.useCarrierPaperClaimDefaultOptionControl);
        this.form.addControl('useCarrierMemberIDFormat', this.useCarrierMemberIDFormatControl);
        this.form.addControl('useCarrierGroupIDFormat', this.useCarrierGroupIDFormatControl);
        this.form.addControl('useCarrierAutoWriteoffCreditType', this.useCarrierAutoWriteoffCreditTypeControl);
        this.form.addControl('userCarrierReferralRequired', this.userCarrierReferralRequiredControl);
        this.form.addControl('useCarrierNoNetwork', this.useCarrierNoNetworkControl);
    }

    private setForm(): void {
        // Member Id Format Control
        this.memberIdFormatControl.setValidators(this.fcListValidation.getValidators('MemberIDFormat'));
        this.memberIdFormatControl.setValue(this.listItem.MemberIDFormat);

        // Group Id Format Control
        this.groupIdFormatControl.setValidators(this.fcListValidation.getValidators('GroupIDFormat'));
        this.groupIdFormatControl.setValue(this.listItem.GroupIDFormat);

        this.useInsuranceCarrierAccountTypeControl.setValue(this.listItem.UseInsuranceCarrierAccountType);
        this.useProviderFeeScheduleControl.setValue(this.listItem.UseProviderFeeSchedule);
        this.useCarrierFeeScheduleControl.setValue(this.listItem.UseCarrierFeeSchedule);
        this.useCarrierAmountAllowedFeeScheduleControl.setValue(this.listItem.UseCarrierAmountAllowedFeeSchedule);
        this.useCarrierPaperClaimFormatControl.setValue(this.listItem.UseCarrierPaperClaimFormat);
        this.useCarrierElectronicClaimFormatControl.setValue(this.listItem.UseCarrierElectronicClaimFormat);
        this.useCarrierPaperClaimDefaultOptionControl.setValue(this.listItem.UseCarrierPaperClaimDefaultOption);
        this.useCarrierMemberIDFormatControl.setValue(this.listItem.UseCarrierMemberIDFormat);
        this.useCarrierGroupIDFormatControl.setValue(this.listItem.UseCarrierGroupIDFormat);
        this.useCarrierAutoWriteoffCreditTypeControl.setValue(this.listItem.UseCarrierAutoWriteoffCreditType);
        this.userCarrierReferralRequiredControl.setValue(this.listItem.UserCarrierReferralRequired);
        this.useCarrierNoNetworkControl.setValue(this.listItem.UseCarrierNoNetwork);

        // Referral Required Control
        this.referralRequiredControl.setValue(this.listItem.ReferralRequired);
        // Network Control
        this.noNetworkControl.setValue(this.listItem.ReferralRequired);
        this.selectedAccountTypeControlPks = this.getFindControlValue(this.listItem.AccountTypeUid);
        this.selectedFeeScheduleControlPks = this.getFindControlValue(this.listItem.FeeScheduleUid);
        this.selectedAmountAllowedListControlPks = this.getFindControlValue(this.listItem.AmountAllowedFeeScheduleUid);
        this.selectedPaperClaimOptionsControlPks = this.getFindControlValue(this.listItem.PaperClaimFormatUid);
        this.selectedElectronicClaimOptionsControlPks = this.getFindControlValue(this.listItem.ElectronicClaimFormatUid);
        this.selectedClaimDefaultOptionsControlPks = this.getFindControlValue(this.listItem.PaperClaimDefaultOptionUid);
        this.selectedReimbursementRuleControlPks = this.getFindControlValue(this.listItem.ReimbursementRuleUid);
        this.selectedAutoWriteoffAdjTypeControlPks = this.getFindControlValue(this.listItem.AutoWriteoffCreditTypeUid);
        this.disableForms();
    }

    private setDefaultValuesIntoForm(): void {
        if (this.itemContext.mode === FcItemModalMode.New) {
            this.setForm();
        }
    }

    private disableForms(): void {
        if (this.listItem.UseInsuranceCarrierAccountType) {
            this.accountTypeUidControl.disable();
        }
        if (this.listItem.UseProviderFeeSchedule || this.listItem.UseCarrierFeeSchedule) {
            this.feeScheduleUidControl.disable();
        }
        if (this.listItem.UseCarrierAmountAllowedFeeSchedule) {
            this.amountAllowedFeeScheduleUidControl.disable();
        }
        if (!this.listItem.ReimbursementRuleUid || this.listItem.ReimbursementRuleUid === this.systemGuid.GuidEmpty) {
            this.reimbursementRuleUidControl.disable();
        }
        if (this.listItem.UseCarrierAutoWriteoffCreditType) {
            this.autoWriteoffCreditTypeUidControl.disable();
        }
        if (this.listItem.UseCarrierPaperClaimFormat) {
            this.paperClaimFormatUidControl.disable();
        }
        if (this.listItem.UseCarrierElectronicClaimFormat) {
            this.electronicClaimFormatUidControl.disable();
        }
        if (this.listItem.UseCarrierPaperClaimDefaultOption) {
            this.paperClaimDefaultOptionUidControl.disable();
        }
        if (this.listItem.UserCarrierReferralRequired) {
            this.referralRequiredControl.disable();
        }
        if (this.listItem.UseCarrierNoNetwork) {
            this.noNetworkControl.disable();
        }
        if (this.listItem.UseCarrierMemberIDFormat) {
            this.memberIdFormatControl.disable();
        }
        if (this.listItem.UseCarrierGroupIDFormat) {
            this.groupIdFormatControl.disable();
        }
        this.checkPayersFee = this.listItem.UseCarrierFeeSchedule;
        this.checkProvidersFee = this.listItem.UseProviderFeeSchedule;
    }
}
